import React, { useState, useRef, useEffect } from "react";
import MainLayout from "../../Layout/mainLayout";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Upload, message } from "antd";
import { LoadingOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { UpdateBlog, ViewBlog } from "../../Redux/Actions/blogActions";
import AddImage from "../../Global Functions/addImage";
import toast from "react-hot-toast";
import "./addBlog.css";

const CATEGORY_OPTIONS = [
    { value: "trending", label: "Trending" },
    { value: "most_read", label: "Most Read" },
];

const execCmd = (cmd, value = null) => document.execCommand(cmd, false, value);

const RichEditor = ({ value, onChange, hasError }) => {
    const ref = useRef(null);
    const [htmlMode, setHtmlMode] = useState(false);
    const [htmlSource, setHtmlSource] = useState("");
    const savedSelectionRef = useRef(null);

    useEffect(() => {
        if (!htmlMode && ref.current) {
            if (ref.current.innerHTML !== value) {
                ref.current.innerHTML = value || "";
            }
        }
    }, [value, htmlMode]);

    const syncContent = () => {
        if (ref.current) {
            onChange(ref.current.innerHTML);
        }
    };

    const saveSelection = () => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
            savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
        }
    };

    const restoreSelection = () => {
        const sel = window.getSelection();
        if (sel && savedSelectionRef.current) {
            sel.removeAllRanges();
            sel.addRange(savedSelectionRef.current);
        }
    };

    const handleToolbarAction = (e, cmd, val = null) => {
        e.preventDefault();
        execCmd(cmd, val);
        syncContent();
        ref.current?.focus();
    };

    const handleLink = (e) => {
        e.preventDefault();
        const url = prompt("Enter URL:");
        if (url) {
            execCmd("createLink", url);
            syncContent();
            ref.current?.focus();
        }
    };

    const handleInsertImage = (e) => {
        e.preventDefault();
        const url = prompt("Enter image URL:");
        if (url) {
            execCmd("insertImage", url);
            syncContent();
            ref.current?.focus();
        }
    };

    const handleInsertTable = (e) => {
        e.preventDefault();
        const rows = parseInt(prompt("Number of rows:", "2") || "2", 10);
        const cols = parseInt(prompt("Number of columns:", "3") || "3", 10);
        if (!rows || !cols) return;
        let tableHtml = '<table border="1" style="border-collapse:collapse;width:100%;margin:8px 0;">';
        for (let r = 0; r < rows; r++) {
            tableHtml += "<tr>";
            for (let c = 0; c < cols; c++) {
                tableHtml += r === 0
                    ? '<th style="border:1px solid #ccc;padding:8px;background:#f3f4f6;">&nbsp;</th>'
                    : '<td style="border:1px solid #ccc;padding:8px;">&nbsp;</td>';
            }
            tableHtml += "</tr>";
        }
        tableHtml += "</table>";
        execCmd("insertHTML", tableHtml);
        syncContent();
        ref.current?.focus();
    };

    const handleInsertHr = (e) => {
        e.preventDefault();
        execCmd("insertHTML", "<hr/>");
        syncContent();
        ref.current?.focus();
    };

    const handleFontSize = (e) => {
        const size = e.target.value;
        if (size) {
            execCmd("fontSize", size);
            syncContent();
        }
        e.target.value = "";
    };

    const handleTextColor = (e) => {
        restoreSelection();
        execCmd("foreColor", e.target.value);
        syncContent();
        ref.current?.focus();
    };

    const handleBgColor = (e) => {
        restoreSelection();
        execCmd("hiliteColor", e.target.value);
        syncContent();
        ref.current?.focus();
    };

    const toggleHtmlMode = () => {
        if (!htmlMode) {
            const currentHtml = ref.current ? ref.current.innerHTML : value || "";
            setHtmlSource(currentHtml);
            setHtmlMode(true);
        } else {
            onChange(htmlSource);
            setHtmlMode(false);
            requestAnimationFrame(() => {
                if (ref.current) {
                    ref.current.innerHTML = htmlSource;
                }
            });
        }
    };

    const handleHtmlSourceChange = (e) => {
        setHtmlSource(e.target.value);
    };

    return (
        <div className={`ab2-rich-wrap ${hasError ? "ab2-input--error" : ""}`}>
            <div className="ab2-rich-toolbar">
                {!htmlMode && (
                    <>
                        {[
                            { cmd: "bold", icon: <b>B</b>, title: "Bold" },
                            { cmd: "italic", icon: <i>I</i>, title: "Italic" },
                            { cmd: "underline", icon: <u>U</u>, title: "Underline" },
                            { cmd: "strikeThrough", icon: <s>S</s>, title: "Strikethrough" },
                        ].map(({ cmd, icon, title }) => (
                            <button
                                key={cmd}
                                type="button"
                                title={title}
                                className="ab2-rich-btn"
                                onMouseDown={(e) => handleToolbarAction(e, cmd)}
                            >
                                {icon}
                            </button>
                        ))}

                        <div className="ab2-rich-divider" />

                        {[
                            { cmd: "insertUnorderedList", icon: "• List", title: "Bullet List" },
                            { cmd: "insertOrderedList", icon: "1. List", title: "Numbered List" },
                        ].map(({ cmd, icon, title }) => (
                            <button
                                key={cmd}
                                type="button"
                                title={title}
                                className="ab2-rich-btn"
                                onMouseDown={(e) => handleToolbarAction(e, cmd)}
                            >
                                {icon}
                            </button>
                        ))}

                        <div className="ab2-rich-divider" />

                        {[
                            { cmd: "justifyLeft", icon: "⬅", title: "Align Left" },
                            { cmd: "justifyCenter", icon: "≡", title: "Center" },
                            { cmd: "justifyRight", icon: "➡", title: "Align Right" },
                            { cmd: "justifyFull", icon: "☰", title: "Justify" },
                        ].map(({ cmd, icon, title }) => (
                            <button
                                key={cmd}
                                type="button"
                                title={title}
                                className="ab2-rich-btn"
                                onMouseDown={(e) => handleToolbarAction(e, cmd)}
                            >
                                {icon}
                            </button>
                        ))}

                        <div className="ab2-rich-divider" />

                        {[
                            { cmd: "outdent", icon: "⇤", title: "Decrease Indent" },
                            { cmd: "indent", icon: "⇥", title: "Increase Indent" },
                        ].map(({ cmd, icon, title }) => (
                            <button
                                key={cmd}
                                type="button"
                                title={title}
                                className="ab2-rich-btn"
                                onMouseDown={(e) => handleToolbarAction(e, cmd)}
                            >
                                {icon}
                            </button>
                        ))}

                        <div className="ab2-rich-divider" />

                        <select
                            className="ab2-rich-select"
                            defaultValue=""
                            onChange={(e) => {
                                handleToolbarAction(e, "formatBlock", e.target.value);
                                e.target.value = "";
                            }}
                        >
                            <option value="" disabled>Heading</option>
                            <option value="h1">H1</option>
                            <option value="h2">H2</option>
                            <option value="h3">H3</option>
                            <option value="h4">H4</option>
                            <option value="h5">H5</option>
                            <option value="h6">H6</option>
                            <option value="p">Paragraph</option>
                            <option value="blockquote">Blockquote</option>
                            <option value="pre">Code Block</option>
                        </select>

                        <select
                            className="ab2-rich-select"
                            defaultValue=""
                            onChange={handleFontSize}
                            title="Font Size"
                        >
                            <option value="" disabled>Size</option>
                            <option value="1">8pt</option>
                            <option value="2">10pt</option>
                            <option value="3">12pt</option>
                            <option value="4">14pt</option>
                            <option value="5">18pt</option>
                            <option value="6">24pt</option>
                            <option value="7">36pt</option>
                        </select>

                        <div className="ab2-rich-divider" />

                        <div className="ab2-rich-color-wrap" title="Text Color" onMouseDown={saveSelection}>
                            <span className="ab2-rich-color-label">A</span>
                            <input
                                type="color"
                                className="ab2-rich-color-input"
                                defaultValue="#111827"
                                onChange={handleTextColor}
                                title="Text Color"
                            />
                        </div>

                        <div className="ab2-rich-color-wrap" title="Highlight Color" onMouseDown={saveSelection}>
                            <span className="ab2-rich-color-label ab2-rich-color-label--bg">▌</span>
                            <input
                                type="color"
                                className="ab2-rich-color-input"
                                defaultValue="#ffff00"
                                onChange={handleBgColor}
                                title="Highlight Color"
                            />
                        </div>

                        <div className="ab2-rich-divider" />

                        <button
                            type="button"
                            className="ab2-rich-btn"
                            title="Insert Link"
                            onMouseDown={handleLink}
                        >
                            🔗
                        </button>

                        <button
                            type="button"
                            className="ab2-rich-btn"
                            title="Insert Image"
                            onMouseDown={handleInsertImage}
                        >
                            🖼
                        </button>

                        <button
                            type="button"
                            className="ab2-rich-btn"
                            title="Insert Table"
                            onMouseDown={handleInsertTable}
                        >
                            ⊞
                        </button>

                        <button
                            type="button"
                            className="ab2-rich-btn"
                            title="Insert Horizontal Rule"
                            onMouseDown={handleInsertHr}
                        >
                            ―
                        </button>

                        <div className="ab2-rich-divider" />

                        <button
                            type="button"
                            className="ab2-rich-btn"
                            title="Undo"
                            onMouseDown={(e) => handleToolbarAction(e, "undo")}
                        >
                            ↩
                        </button>

                        <button
                            type="button"
                            className="ab2-rich-btn"
                            title="Redo"
                            onMouseDown={(e) => handleToolbarAction(e, "redo")}
                        >
                            ↪
                        </button>

                        <button
                            type="button"
                            className="ab2-rich-btn ab2-rich-btn--danger"
                            title="Clear formatting"
                            onMouseDown={(e) => handleToolbarAction(e, "removeFormat")}
                        >
                            ✕
                        </button>
                    </>
                )}

                <button
                    type="button"
                    className={`ab2-rich-btn ab2-rich-btn--html ${htmlMode ? "ab2-rich-btn--html-active" : ""}`}
                    title={htmlMode ? "Switch to Visual Editor" : "Edit HTML Source"}
                    onMouseDown={(e) => { e.preventDefault(); toggleHtmlMode(); }}
                >
                    {htmlMode ? "Visual" : "</>"} 
                </button>
            </div>

            {htmlMode ? (
                <textarea
                    className="ab2-rich-html-source"
                    value={htmlSource}
                    onChange={handleHtmlSourceChange}
                    spellCheck={false}
                    placeholder="Write or paste HTML source here…"
                />
            ) : (
                <div
                    ref={ref}
                    contentEditable
                    suppressContentEditableWarning
                    className="ab2-rich-editor"
                    data-placeholder="Write section content here…"
                    onBlur={syncContent}
                    onMouseUp={saveSelection}
                    onKeyUp={saveSelection}
                />
            )}
        </div>
    );
};

const EditBlogPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const { blog, loading: storeLoading } = useSelector((state) => state.blog);

    const [formData, setFormData] = useState({ title: "", description: "", category: "" });
    const [blogContents, setBlogContents] = useState([{ content: "", image: "", imageUploading: false, uid: Date.now() }]);
    const [authors, setAuthors] = useState([{ author_name: "", author_image: "", author_bio: "", imageUploading: false, uid: Date.now() + 1 }]);
    const [blogImages, setBlogImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (id) dispatch(ViewBlog(id));
    }, [id, dispatch]);

    useEffect(() => {
        if (blog) {
            setFormData({
                title: blog.title || "",
                description: blog.description || "",
                category: blog.category || "",
            });

            if (blog.blog_content && Array.isArray(blog.blog_content)) {
                setBlogContents(blog.blog_content.map((section, index) => ({
                    content: section.content || "",
                    image: section.image || "",
                    imageUploading: false,
                    uid: `existing-${index}`,
                })));
            } else if (blog.blog_content) {
                setBlogContents([{ content: blog.blog_content, image: "", imageUploading: false, uid: "existing-0" }]);
            }

            if (blog.author && Array.isArray(blog.author)) {
                setAuthors(blog.author.map((author, index) => ({
                    author_name: author.author_name || "",
                    author_image: author.author_image || "",
                    author_bio: author.author_bio || "",
                    imageUploading: false,
                    uid: `existing-author-${index}`,
                })));
            }

            if (blog.cover_images) {
                const imageUrls = blog.cover_images.split(",").filter(Boolean);
                setBlogImages(imageUrls.map((url, index) => ({
                    uid: `existing-${index}`,
                    url,
                    status: "done",
                    name: `image-${index}`,
                })));
            }
        }
    }, [blog]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const addContentBlock = () =>
        setBlogContents((prev) => [...prev, { content: "", image: "", imageUploading: false, uid: Date.now() }]);

    const removeContentBlock = (uid) =>
        setBlogContents((prev) => prev.filter((b) => b.uid !== uid));

    const updateContentBlock = (uid, field, value) =>
        setBlogContents((prev) => prev.map((b) => (b.uid === uid ? { ...b, [field]: value } : b)));

    const uploadContentImage = async ({ file, onSuccess, onError }, uid) => {
        updateContentBlock(uid, "imageUploading", true);
        try {
            const url = await AddImage(file);
            onSuccess({ url }, file);
            updateContentBlock(uid, "image", url);
            updateContentBlock(uid, "imageUploading", false);
        } catch (err) {
            message.error("Image upload failed!");
            onError(err);
            updateContentBlock(uid, "imageUploading", false);
        }
    };

    const addAuthor = () =>
        setAuthors((prev) => [...prev, { author_name: "", author_image: "", author_bio: "", imageUploading: false, uid: Date.now() }]);

    const removeAuthor = (uid) =>
        setAuthors((prev) => prev.filter((a) => a.uid !== uid));

    const updateAuthor = (uid, field, value) =>
        setAuthors((prev) => prev.map((a) => (a.uid === uid ? { ...a, [field]: value } : a)));

    const uploadAuthorImage = async ({ file, onSuccess, onError }, uid) => {
        updateAuthor(uid, "imageUploading", true);
        try {
            const url = await AddImage(file);
            onSuccess({ url }, file);
            updateAuthor(uid, "author_image", url);
            updateAuthor(uid, "imageUploading", false);
        } catch (err) {
            message.error("Author image upload failed!");
            onError(err);
            updateAuthor(uid, "imageUploading", false);
        }
    };

    const customImageUpload = async ({ file, onSuccess, onError }) => {
        try {
            setUploading(true);
            const url = await AddImage(file);
            onSuccess({ url }, file);
            setBlogImages((prev) => [...prev, { uid: file.uid, url, status: "done", name: file.name }]);
        } catch (err) {
            message.error("Image upload failed!");
            onError(err);
        } finally {
            setUploading(false);
        }
    };

    const handleRemoveImage = (uid) =>
        setBlogImages((prev) => prev.filter((img) => img.uid !== uid));

    const isInvalid = (val) => submitted && !val?.trim();

    const handleSubmit = async () => {
        setSubmitted(true);
        const hasEmptyContent = blogContents.some((b) => !b.content?.trim());
        if (!formData.title || !formData.description || !formData.category || hasEmptyContent) {
            toast.error("Please fill all required fields");
            return;
        }

        const payload = {
            id,
            title: formData.title,
            description: formData.description,
            category: formData.category,
            cover_images: blogImages.map((img) => img.url).join(","),
            blog_content: blogContents.map(({ content, image }) => ({ content, image })),
            author: authors
                .filter((a) => a.author_name.trim())
                .map(({ author_name, author_image, author_bio }) => ({ author_name, author_image, author_bio })),
        };

        try {
            setLoading(true);
            const result = await dispatch(UpdateBlog(payload));
            if (result) {
                toast.success("Blog updated successfully");
                navigate("/blog");
            }
        } catch (error) {
            toast.error(error?.message || "Failed to update blog");
        } finally {
            setLoading(false);
        }
    };

    if (storeLoading && !blog) {
        return (
            <MainLayout>
                <div className="ab2-wrapper" style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af", fontSize: "14px" }}>
                    <LoadingOutlined style={{ fontSize: 24, marginBottom: 12 }} />
                    <p>Loading blog data…</p>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="ab2-wrapper">

                <div className="ab2-header">
                    <div className="ab2-header-left">
                        <div className="ab2-header-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="ab2-title">Edit Blog</h1>
                            <p className="ab2-subtitle">Update and republish your blog post</p>
                        </div>
                    </div>
                    <button className="ab2-view-btn" onClick={() => navigate("/blog")}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                        View Blog List
                    </button>
                </div>

                <div className="ab2-body">

                    <div className="ab2-main">

                        <div className="ab2-card">
                            <h3 className="ab2-card-header">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 6h16M4 12h10M4 18h6" />
                                </svg>
                                Blog Title
                            </h3>
                            <div className="ab2-field">
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    placeholder="Enter a compelling blog title…"
                                    className={`ab2-input ${isInvalid(formData.title) ? "ab2-input--error" : ""}`}
                                />
                                {isInvalid(formData.title) && <span className="ab2-error-msg">Title is required</span>}
                            </div>
                        </div>

                        <div className="ab2-card">
                            <h3 className="ab2-card-header">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                </svg>
                                Short Description
                            </h3>
                            <div className="ab2-field">
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows={3}
                                    placeholder="Write a brief summary shown in listing cards…"
                                    className={`ab2-textarea ${isInvalid(formData.description) ? "ab2-input--error" : ""}`}
                                />
                                {isInvalid(formData.description) && <span className="ab2-error-msg">Description is required</span>}
                            </div>
                        </div>

                        <div className="ab2-card">
                            <div className="ab2-card-header-row">
                                <h3 className="ab2-card-header" style={{ margin: 0 }}>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
                                    </svg>
                                    Blog Content
                                </h3>
                                <button type="button" className="ab2-add-block-btn" onClick={addContentBlock}>
                                    <PlusOutlined /> Add Section
                                </button>
                            </div>

                            {blogContents.map((block, idx) => (
                                <div key={block.uid} className="ab2-content-block">
                                    <div className="ab2-block-header">
                                        <span className="ab2-block-label">Section {idx + 1}</span>
                                        {blogContents.length > 1 && (
                                            <button type="button" className="ab2-block-remove" onClick={() => removeContentBlock(block.uid)}>
                                                <DeleteOutlined /> Remove
                                            </button>
                                        )}
                                    </div>

                                    <div className="ab2-field" style={{ marginBottom: 12 }}>
                                        <RichEditor
                                            value={block.content}
                                            onChange={(val) => updateContentBlock(block.uid, "content", val)}
                                            hasError={submitted && !block.content?.trim()}
                                        />
                                        {submitted && !block.content?.trim() && (
                                            <span className="ab2-error-msg">Section content is required</span>
                                        )}
                                    </div>

                                    <div className="ab2-field">
                                        <label className="ab2-field-label">Section Image (optional)</label>
                                        {block.image ? (
                                            <div className="ab2-section-img-wrap">
                                                <img src={block.image} alt="section" className="ab2-section-img" />
                                                <button
                                                    type="button"
                                                    className="ab2-section-img-remove"
                                                    onClick={() => updateContentBlock(block.uid, "image", "")}
                                                >
                                                    <DeleteOutlined /> Remove
                                                </button>
                                            </div>
                                        ) : (
                                            <Upload
                                                customRequest={(opts) => uploadContentImage(opts, block.uid)}
                                                showUploadList={false}
                                                accept="image/*"
                                            >
                                                <button type="button" className="ab2-upload-btn">
                                                    {block.imageUploading ? <LoadingOutlined /> : <PlusOutlined />}
                                                    {block.imageUploading ? "Uploading…" : "Upload Section Image"}
                                                </button>
                                            </Upload>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="ab2-card">
                            <div className="ab2-card-header-row">
                                <h3 className="ab2-card-header" style={{ margin: 0 }}>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                                    </svg>
                                    Authors
                                </h3>
                                <button type="button" className="ab2-add-block-btn" onClick={addAuthor}>
                                    <PlusOutlined /> Add Author
                                </button>
                            </div>

                            {authors.map((author, idx) => (
                                <div key={author.uid} className="ab2-content-block">
                                    <div className="ab2-block-header">
                                        <span className="ab2-block-label">Author {idx + 1}</span>
                                        {authors.length > 1 && (
                                            <button type="button" className="ab2-block-remove" onClick={() => removeAuthor(author.uid)}>
                                                <DeleteOutlined /> Remove
                                            </button>
                                        )}
                                    </div>

                                    <div className="ab2-author-grid">
                                        <div className="ab2-author-img-col">
                                            {author.author_image ? (
                                                <div className="ab2-author-img-wrap">
                                                    <img src={author.author_image} alt="author" className="ab2-author-img" />
                                                    <button
                                                        type="button"
                                                        className="ab2-image-remove"
                                                        onClick={() => updateAuthor(author.uid, "author_image", "")}
                                                    >
                                                        <DeleteOutlined />
                                                    </button>
                                                </div>
                                            ) : (
                                                <Upload
                                                    customRequest={(opts) => uploadAuthorImage(opts, author.uid)}
                                                    showUploadList={false}
                                                    accept="image/*"
                                                >
                                                    <div className="ab2-author-img-placeholder">
                                                        {author.imageUploading ? <LoadingOutlined /> : <PlusOutlined />}
                                                        <span>{author.imageUploading ? "Uploading…" : "Photo"}</span>
                                                    </div>
                                                </Upload>
                                            )}
                                        </div>

                                        <div className="ab2-author-fields">
                                            <div className="ab2-field">
                                                <input
                                                    type="text"
                                                    placeholder="Author Name"
                                                    value={author.author_name}
                                                    onChange={(e) => updateAuthor(author.uid, "author_name", e.target.value)}
                                                    className="ab2-input"
                                                />
                                            </div>
                                            <div className="ab2-field">
                                                <textarea
                                                    placeholder="Author Bio"
                                                    value={author.author_bio}
                                                    onChange={(e) => updateAuthor(author.uid, "author_bio", e.target.value)}
                                                    rows={2}
                                                    className="ab2-textarea"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                    </div>

                    <div className="ab2-sidebar">

                        <div className="ab2-card">
                            <h3 className="ab2-card-header">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                                </svg>
                                Category
                            </h3>
                            <div className="ab2-field">
                                <div className={`ab2-select-wrap ${submitted && !formData.category ? "ab2-input--error" : ""}`}>
                                    <select name="category" value={formData.category} onChange={handleChange} className="ab2-select">
                                        <option value="" disabled>Select category…</option>
                                        {CATEGORY_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                    <svg className="ab2-select-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="6 9 12 15 18 9" />
                                    </svg>
                                </div>
                                {submitted && !formData.category && <span className="ab2-error-msg">Category is required</span>}
                            </div>
                        </div>

                        <div className="ab2-card">
                            <h3 className="ab2-card-header">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
                                </svg>
                                Cover Images
                            </h3>
                            <p className="ab2-hint">Upload cover images for the blog post</p>

                            {blogImages.length > 0 && (
                                <div className="ab2-image-grid">
                                    {blogImages.map((img) => (
                                        <div key={img.uid} className="ab2-image-item">
                                            <img src={img.url} alt="blog" className="ab2-image-thumb" />
                                            <button type="button" className="ab2-image-remove" onClick={() => handleRemoveImage(img.uid)}>
                                                <DeleteOutlined />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <Upload customRequest={customImageUpload} showUploadList={false} accept="image/*" multiple>
                                <button type="button" className="ab2-upload-btn">
                                    {uploading ? <LoadingOutlined /> : <PlusOutlined />}
                                    {uploading ? "Uploading…" : "Add Image"}
                                </button>
                            </Upload>
                        </div>

                        <div className="ab2-actions">
                            <button type="button" className="ab2-btn ab2-btn--cancel" onClick={() => navigate("/blog")} disabled={loading}>
                                Cancel
                            </button>
                            <button type="button" className="ab2-btn ab2-btn--submit" onClick={handleSubmit} disabled={loading || uploading}>
                                {loading ? (
                                    <><LoadingOutlined /> Saving…</>
                                ) : (
                                    <>
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                        Update Blog
                                    </>
                                )}
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditBlogPage;